import { useLazyQuery } from "@apollo/client";
import { useEffect, useRef, useState } from "react";
import { Box, Button, Card, Flex, Image, Text } from "rebass/styled-components";
import styled from "styled-components";
import { Skeleton } from "../../../../components/loadingSkeleton/Skeleton";
import PleaseWait from "../../../../components/PleaseWait";
import Advocado from '../../../../static/Advocado.png'
import { InputNoForm } from "../../../../components/TextInput";
import { GET_AIP_RECIPES_BY_FILTER } from "../../../../graphql/queries";
import { IGetAipRecipesByFilterFromWebInput, IGetAipRecipesByFilterFromWebOutput, IThumbnailAipRecipeOutput } from '../../../../graphql/types/queries/recipes'


const CategoryTag = styled(Text)`
  transition: 0.3s all ease-in-out;
  text-decoration: underline, 
  cursor: pointer,
  font-weight: 300;
  font-size: 14px;
  color: #F8A294;

  &:hover {
  font-weight: 500;
  }
`;

const BoxRight = styled(Box)`
  width: 254px;

  @media screen and (max-width: 68.8125em) {
    display: none;
  }
`;

const BoxRightMobile = styled(Flex)`
  flex-direction: column;
  @media screen and (min-width: 68.8125em) {
    display: none;
  }
`;

const Title = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const Img = styled(Image)`
  transition: 0.3s all ease-in-out;
  object-fit: cover;
  &:hover {
    transform: scale(1.1);
  }
`;

const RecipeCardContent = styled(Card)`
  cursor: pointer;
  margin-right: 24px;
  &:hover ${Img} {
    transform: scale(1.15);
  }
  @media screen and (max-width: 480px) {
    &.individual {
      margin-right: 0px;
    }
  }
`;


const CategoryTagMobile = styled(Box)`
  transition: 0.3s all ease-in-out;
  border-radius: 24px;
  padding: 4px 8px;
  color: #7992b3;
  border: 1px solid #7992b3;
  margin-right: 18px;
  white-space: nowrap;
  cursor: pointer;
  font-weight: 300;

  &.isSelected {
    font-weight: 500;
    color: rgb(53, 78, 121);
    border: 1px solid rgb(53, 78, 121);
  }
`;
const FilterButton = ({ title, isSelected, onPress }: { title: string, isSelected: boolean, onPress: () => void }) => {
  return (
    <Flex onClick={onPress} mb={2} style={{ cursor: 'pointer' }} alignItems="center">
      <Flex style={{ height: 16, width: 16, border: '1px solid #F8A294', background: isSelected ? '#F8A294' : 'transparent', justifyContent: 'center', alignItems: 'center' }} mr={2}>
        {!!isSelected && (
          <svg width="9" height="8" viewBox="0 0 9 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.6925 0L8.8315 0.799L3.893 7.922H2.754L0 4.063L1.139 3.0005L3.3235 5.0405L7.6925 0Z" fill="white" />
          </svg>
        )}
      </Flex>
      <CategoryTag style={{ fontWeight: isSelected ? 600 : '' }}>{title}</CategoryTag>
    </Flex>
  );
};

export const RecipeCardLoading = ({ className }: { className: string }) => {
  return (
    <RecipeCardContent className={className} style={{ minWidth: '150px', width: '150px', height: '208px', marginRight: '24px', overflow: 'hidden', marginBottom: '32px', borderRadius: '10px' }}>
      <Flex flexDirection={'column'} style={{ position: 'relative', height: '100%', paddingTop: '30px' }}>
        <PleaseWait
          text={' '}
        />
        <Flex style={{ height: '50%', left: 0, right: 0, bottom: 0, backgroundColor: 'white', justifyContent: 'center', alignItems: 'center', position: 'absolute', padding: '6px' }}>
          <Skeleton width="75px" height="16px" />
        </Flex>
      </Flex>
    </RecipeCardContent>
  );
}

const RecipeCard = ({ recipe, onClick, className, isSelected, onSelect, canSelect = true }: { canSelect?: boolean, recipe: IThumbnailAipRecipeOutput, onClick: () => void, className: string, isSelected: boolean, onSelect: () => void }) => {
  return (
    <RecipeCardContent className={className} onClick={onClick} style={{ minWidth: '200px', width: '200px', height: '260px', overflow: 'hidden', marginBottom: '32px', borderRadius: '10px' }}>
      <Flex flexDirection={'column'} style={{ position: 'relative', height: '100%' }}>
        <Img src={`${recipe.image}`} style={{ flex: 1, width: '100%', objectFit: 'cover' }} />
        <Flex style={{ height: '92px', left: 0, right: 0, bottom: 0, backgroundColor: 'white', justifyContent: 'center', alignItems: 'center', position: 'absolute', padding: '6px' }}>
          <Title textAlign={'center'} fontSize='14px'>{recipe.name}</Title>
        </Flex>
        {!!canSelect && (
          <Flex onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onSelect()
          }} style={{ paddingBottom: '8px', position: 'absolute', right: 0, top: 0, width: '80px', height: '55px', justifyContent: 'center', alignItems: 'flex-end', transformOrigin: 'bottom right', transform: 'rotate(45deg)', background: !isSelected ? 'white' : '#344F7A', boxShadow: '0px 4px 20px rgb(0 0 0 / 33%)' }}>
            {isSelected ? (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M5 0C3.67392 0 2.40215 0.526784 1.46447 1.46447C0.526784 2.40215 0 3.67392 0 5V15C0 16.3261 0.526784 17.5979 1.46447 18.5355C2.40215 19.4732 3.67392 20 5 20H15C16.3261 20 17.5979 19.4732 18.5355 18.5355C19.4732 17.5979 20 16.3261 20 15V5C20 3.67392 19.4732 2.40215 18.5355 1.46447C17.5979 0.526784 16.3261 0 15 0H5ZM13.73 8.684C13.8198 8.58814 13.8899 8.47552 13.9362 8.35257C13.9825 8.22963 14.0041 8.09878 13.9998 7.96747C13.9956 7.83617 13.9655 7.707 13.9113 7.58732C13.8571 7.46765 13.7799 7.35982 13.684 7.27C13.5881 7.18018 13.4755 7.11011 13.3526 7.06381C13.2296 7.01751 13.0988 6.99588 12.9675 7.00015C12.8362 7.00442 12.707 7.03451 12.5873 7.08871C12.4677 7.1429 12.3598 7.22014 12.27 7.316L9.187 10.606L7.664 9.253C7.46446 9.08712 7.20825 9.0053 6.9495 9.02482C6.69075 9.04434 6.44972 9.16368 6.27732 9.35762C6.10493 9.55156 6.01467 9.80492 6.02562 10.0642C6.03657 10.3234 6.14787 10.5683 6.336 10.747L8.586 12.747C8.78069 12.9199 9.03515 13.0101 9.29529 12.9983C9.55543 12.9866 9.80072 12.8738 9.979 12.684L13.729 8.684H13.73Z" fill="white" />
            </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 3H7C4.79086 3 3 4.79086 3 7V17C3 19.2091 4.79086 21 7 21H17C19.2091 21 21 19.2091 21 17V7C21 4.79086 19.2091 3 17 3Z" stroke="#344F7A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            )}
          </Flex>
        )}
      </Flex>
    </RecipeCardContent>
  );
};
const Recipes = ({ selectedRecipes, setSelectedRecipes, viewRecipe, canSelect = true }: { canSelect?: boolean, selectedRecipes: Array<IThumbnailAipRecipeOutput | null>, setSelectedRecipes: (recipe: IThumbnailAipRecipeOutput) => void, viewRecipe: (recipe: IThumbnailAipRecipeOutput) => void }) => {
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [excludeAllergens, setExcludeAllergens] = useState<Array<string>>([]);
  const [filter, setFilter] = useState(null);
  const [filterTmp, setFilterTmp] = useState(null);
  const containerRef = useRef<any>(null);
  const [query, { data, loading, fetchMore }] = useLazyQuery<IGetAipRecipesByFilterFromWebOutput, IGetAipRecipesByFilterFromWebInput>(GET_AIP_RECIPES_BY_FILTER, {
    variables: {
      excludedAllergens: excludeAllergens,
      filterText: filter,
      idsToExclude: [],
    }
  })

  useEffect(() => {
    query({
      variables: {
        excludedAllergens: excludeAllergens,
        filterText: filter,
        idsToExclude: [],
      }
    })
  }, [])

  useEffect(() => {
    if (containerRef && containerRef.current) {
      containerRef.current.scrollTo(0, 0);
    }
    query({
      variables: {
        excludedAllergens: excludeAllergens,
        filterText: filter,
        idsToExclude: [],
      }
    })
  }, [filter, excludeAllergens])


  const excludeAllergensPressHandler = (name: string | null) => {
    let allergens = [];
    if (!name) {
      allergens = [];
      setExcludeAllergens([]);
    } else {
      if (name === 'FAVORITES') {
        if (excludeAllergens.indexOf(name) < 0) {
          allergens = [name];
          setExcludeAllergens([name]);
        }
      } else if (excludeAllergens.indexOf(name) < 0) {
        allergens = [...excludeAllergens.filter((x) => x !== 'FAVORITES'), name];
        setExcludeAllergens(allergens);
      } else {
        const filt = excludeAllergens.filter((x) => x !== name && x !== 'FAVORITES');
        allergens = filt;
        setExcludeAllergens(filt);
      }
    }
    setIsFetchingMore(false);
  };

  const onScroll = (e: any) => {
    if (!!data && data.getAipRecipesByFilterFromWeb && !isFetchingMore) {
      if (e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight) {
        if (data.getAipRecipesByFilterFromWeb.length % 24 !== 0) return;
        setTimeout(() => {
          onFetchMore();
        }, 300);

        return;
      }
    }
  };

  const onFetchMore = () => {
    if (!data || !data.getAipRecipesByFilterFromWeb || data.getAipRecipesByFilterFromWeb.length < 1) return;
    setIsFetchingMore(true);
    fetchMore({
      variables: {
        excludedAllergens: excludeAllergens,
        filterText: filter,
        idsToExclude: [...data.getAipRecipesByFilterFromWeb.filter(x => !!x).map((x) => x!.id)],
      },
      updateQuery: (previousResult: any, { fetchMoreResult }) => {
        setIsFetchingMore(false);
        if (!fetchMoreResult || !fetchMoreResult.getAipRecipesByFilterFromWeb || fetchMoreResult.getAipRecipesByFilterFromWeb.length < 1) {
          return previousResult;
        }
        return Object.assign({}, previousResult, {
          getAipRecipesByFilterFromWeb: [...previousResult.getAipRecipesByFilterFromWeb, ...fetchMoreResult.getAipRecipesByFilterFromWeb],
        });
      },
    });
  };
  return (
    <>

      <BoxRightMobile>
        <Flex flexDirection={'row'} mt={4} marginX={[4,0,0]} style={{
          background: 'white',
          borderRadius: '12px', padding: '0 6px 0 12px', height: '51px',
          border: '1px solid #344F7A'
        }} alignItems='center'
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" >
            <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 12.7543 18.1045 14.9163 16.5104 16.5104C14.9163 18.1045 12.7543 19 10.5 19C8.24566 19 6.08365 18.1045 4.48959 16.5104C2.89553 14.9163 2 12.7543 2 10.5C2 8.24566 2.89553 6.08365 4.48959 4.48959C6.08365 2.89553 8.24566 2 10.5 2C12.7543 2 14.9163 2.89553 16.5104 4.48959C18.1045 6.08365 19 8.24566 19 10.5V10.5Z" stroke="#344F7A" stroke-width="2" stroke-linecap="round" />
          </svg>
          <InputNoForm
            style={{ backgroundColor: 'white', border: '0px', flex: 1, paddingTop: '12px', paddingBottom: '12px' }}
            containerStyle={{ width: '100%' }}
            placeholder='Search'
            type='search'
            onChange={(e: any) => {
              if (!e.target.value) {
                setFilter(null);
                setFilterTmp(null);
                return;
              }
              setFilterTmp(e.target.value);
            }}
            value={filterTmp}
            onReturn={() => {
              setFilter(filterTmp);
            }} />

        </Flex>
        {!!filterTmp &&
          filterTmp !== filter && (
            <Text mt={2} style={{ fontSize: '12px', fontWeight: 300 }}>
              Press <span style={{ fontWeight: 500 }}>Enter</span> to search
            </Text>
          )}

        <Box style={{ maxWidth: 'calc(100% + 40px)', overflow: 'auto', marginLeft: '-20px', marginRight: '-20px' }}>
          <Flex style={{ paddingTop: '24px', paddingBottom: '24px', paddingLeft: '20px', paddingRight: '20px', width: '1100px' }}>
            <CategoryTagMobile title="All" className={excludeAllergens.length < 1 ? 'isSelected' : ''} onClick={() => excludeAllergensPressHandler(null)} >All</CategoryTagMobile>
            <CategoryTagMobile title="Dairy Free" className={excludeAllergens.indexOf('dairy-free') > -1 ? 'isSelected' : ''} onClick={() => excludeAllergensPressHandler('dairy-free')} >Dairy Free</CategoryTagMobile>
            <CategoryTagMobile title="Gluten Free" className={excludeAllergens.indexOf('gluten-free') > -1 ? 'isSelected' : ''} onClick={() => excludeAllergensPressHandler('gluten-free')} >Gluten Free</CategoryTagMobile>
            <CategoryTagMobile title="Sugar Free" className={excludeAllergens.indexOf('sugar-free') > -1 ? 'isSelected' : ''} onClick={() => excludeAllergensPressHandler('sugar-free')} >Sugar Free</CategoryTagMobile>
            <CategoryTagMobile title="Egg Free" className={excludeAllergens.indexOf('egg-free') > -1 ? 'isSelected' : ''} onClick={() => excludeAllergensPressHandler('egg-free')} >Egg Free</CategoryTagMobile>
            <CategoryTagMobile title="Peanut Free" className={excludeAllergens.indexOf('peanut-free') > -1 ? 'isSelected' : ''} onClick={() => excludeAllergensPressHandler('peanut-free')} >Peanut Free</CategoryTagMobile>
            <CategoryTagMobile title="Tree-Nut Free" className={excludeAllergens.indexOf('treenut-free') > -1 ? 'isSelected' : ''} onClick={() => excludeAllergensPressHandler('treenut-free')} >Tree-Nut Free</CategoryTagMobile>
            <CategoryTagMobile title="Soy Free" className={excludeAllergens.indexOf('soy-free') > -1 ? 'isSelected' : ''} onClick={() => excludeAllergensPressHandler('soy-free')} >Soy Free</CategoryTagMobile>
            <CategoryTagMobile title="Nightshade Free" className={excludeAllergens.indexOf('nightshade-free') > -1 ? 'isSelected' : ''} onClick={() => excludeAllergensPressHandler('nightshade-free')} >Nightshade Free</CategoryTagMobile>
          </Flex>
        </Box>
      </BoxRightMobile>
      <Flex ref={containerRef} flex={1} style={{ overflow: 'auto' }} onScroll={(e) => onScroll(e)}>
        <Flex flex={1} flexWrap={'wrap'} p={'20px'}>
          {!!loading && !isFetchingMore && (!data || !data.getAipRecipesByFilterFromWeb) ? (
            <>
              {[...Array(24)].map((x, i) => {
                return <RecipeCardLoading className="individual" key={`loadingRecipeCat${i}`} />;
              })}
            </>
          ) : (
            <>
              {((data || {}).getAipRecipesByFilterFromWeb || []).map((x) => {
                if (!x) return null
                return (
                  <RecipeCard
                    canSelect={canSelect}
                    key={x.id}
                    onSelect={() => setSelectedRecipes(x)}
                    recipe={x}
                    className="individual"
                    onClick={() => { viewRecipe(x) }}
                    isSelected={selectedRecipes.findIndex(z => z?.id === x.id) > -1}
                  />
                );
              })}

              {!!data && !!data.getAipRecipesByFilterFromWeb && data.getAipRecipesByFilterFromWeb.length === 0 && (
                <Flex style={{ flexDirection: 'column', alignItems: 'center' }}>
                  <img src={Advocado} style={{ maxWidth: '120px' }} />
                  <Text my={3}>We did not find any recipes that match your search</Text>
                  <Button
                    variant={'primary'}
                    onClick={() => {
                      setExcludeAllergens([]);
                      setFilter(null);
                      setFilterTmp(null);
                    }}
                  >
                    Reset Search
                  </Button>
                </Flex>
              )}
              {!!isFetchingMore && (
                <Flex width={'100%'}
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0
                  }}
                  padding={4} backgroundColor='white' opacity={0.85} alignItems='center' justifyContent={'center'}>
                  <PleaseWait text='Loading more' />
                </Flex>
              )}
            </>
          )}
        </Flex>
        <BoxRight>
          <Flex style={{ position: 'fixed' }} flexDirection={'column'}>
            <Flex flexDirection={'row'} mt={4} style={{
              background: 'white',
              borderRadius: '12px', padding: '0 6px 0 12px', height: '51px',
              border: '1px solid #344F7A'
            }} alignItems='center'
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" >
                <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 12.7543 18.1045 14.9163 16.5104 16.5104C14.9163 18.1045 12.7543 19 10.5 19C8.24566 19 6.08365 18.1045 4.48959 16.5104C2.89553 14.9163 2 12.7543 2 10.5C2 8.24566 2.89553 6.08365 4.48959 4.48959C6.08365 2.89553 8.24566 2 10.5 2C12.7543 2 14.9163 2.89553 16.5104 4.48959C18.1045 6.08365 19 8.24566 19 10.5V10.5Z" stroke="#344F7A" stroke-width="2" stroke-linecap="round" />
              </svg>
              <InputNoForm
                style={{ backgroundColor: 'white', border: '0px', flex: 1, paddingTop: '12px', paddingBottom: '12px' }}
                containerStyle={{ width: '100%' }}
                placeholder='Search'
                type='search'
                onChange={(e: any) => {
                  if (!e.target.value) {
                    setFilter(null);
                    setFilterTmp(null);
                    return;
                  }
                  setFilterTmp(e.target.value);
                }}
                value={filterTmp}
                onReturn={() => {
                  setFilter(filterTmp);
                }} />

            </Flex>
            {!!filterTmp &&
              filterTmp !== filter && (
                <Text mt={2} style={{ fontSize: '12px', fontWeight: 300 }}>
                  Press <span style={{ fontWeight: 500 }}>Enter</span> to search
                </Text>
              )}

            <Text style={{ fontWeight: 500 }} mt={3} mb={3}>
              Categories
            </Text>
            <FilterButton title="All" isSelected={excludeAllergens.length < 1} onPress={() => excludeAllergensPressHandler(null)} />
            <FilterButton title="Dairy Free" isSelected={excludeAllergens.indexOf('dairy-free') > -1} onPress={() => excludeAllergensPressHandler('dairy-free')} />

            <FilterButton title="Gluten Free" isSelected={excludeAllergens.indexOf('gluten-free') > -1} onPress={() => excludeAllergensPressHandler('gluten-free')} />

            <FilterButton title="Sugar Free" isSelected={excludeAllergens.indexOf('sugar-free') > -1} onPress={() => excludeAllergensPressHandler('sugar-free')} />

            <FilterButton title="Egg Free" isSelected={excludeAllergens.indexOf('egg-free') > -1} onPress={() => excludeAllergensPressHandler('egg-free')} />

            <FilterButton title="Peanut Free" isSelected={excludeAllergens.indexOf('peanut-free') > -1} onPress={() => excludeAllergensPressHandler('peanut-free')} />

            <FilterButton title="Tree-Nut Free" isSelected={excludeAllergens.indexOf('treenut-free') > -1} onPress={() => excludeAllergensPressHandler('treenut-free')} />

            <FilterButton title="Soy Free" isSelected={excludeAllergens.indexOf('soy-free') > -1} onPress={() => excludeAllergensPressHandler('soy-free')} />

            <FilterButton title="Nightshade Free" isSelected={excludeAllergens.indexOf('nightshade-free') > -1} onPress={() => excludeAllergensPressHandler('nightshade-free')} />
          </Flex>
        </BoxRight>

      </Flex>
    </>
  )
}

export default Recipes